import { formatTranslation, unsafeTranslationKey } from 'intl';
import { IntlShape } from 'react-intl';
import { Field } from 'shared/domain/visibleField/availableFields';
import {
  VisibleFieldModel,
  VisibleFieldsEditModel,
} from 'shared/domain/visibleField/visibleFieldModel';
import { Process } from 'shared/domain/process/types/model';
import { HashMap } from 'shared/types/commonView';
import { FieldOnView } from './types';

/**
 * The edit of the form: fields switched on (or with changed default days) are
 * added, fields switched off are deleted. Form keys are `fieldName` and
 * `processId` joined with `splitChar`.
 */
export function buildVisibleFieldsEdit({
  formState,
  defaultDaysState,
  visibleFieldsByProcess,
  splitChar,
}: {
  formState: Record<string, boolean>;
  defaultDaysState: Record<string, number | null | undefined>;
  visibleFieldsByProcess: HashMap<VisibleFieldModel[]>;
  splitChar: string;
}): VisibleFieldsEditModel {
  const edit: VisibleFieldsEditModel = { adds: [], deletes: [] };
  const addField = (key: string): void => {
    const [fieldName, processId] = key.split(splitChar);
    const days = defaultDaysState[key];
    edit.adds.push({ fieldName, processId, defaultDays: days === null ? undefined : days });
  };

  Object.keys(formState).forEach((key) => {
    if (formState[key]) {
      addField(key);
      return;
    }
    const [fieldName, processId] = key.split(splitChar);
    const hidden = (visibleFieldsByProcess[processId] ?? []).find(
      (visibleField) => visibleField.fieldName === fieldName
    );
    if (hidden) {
      edit.deletes.push(hidden._id);
    }
  });
  Object.keys(defaultDaysState)
    .filter((key) => !(key in formState))
    .forEach(addField);

  return edit;
}

export function addFieldWithDependsOn({
  result,
  field,
  fieldVisibility,
  selectedProcessId,
  intl,
  sourceFieldVisible,
}: {
  result: HashMap<FieldOnView>;
  field: Field;
  fieldVisibility: HashMap<VisibleFieldModel[]>;
  selectedProcessId: Process;
  intl: IntlShape;
  sourceFieldVisible: boolean;
}): void {
  result[field.fieldName] = {
    ...field,
    disabled: !sourceFieldVisible || !field.canBeDisabled,
    visible: !sourceFieldVisible
      ? false
      : isFieldVisible({
          field,
          fieldVisibility,
          selectedProcessId,
        }),
    label: formatTranslation(intl, unsafeTranslationKey(field.labelId) ),
  };
}

export function addField({
  result,
  field,
  fieldVisibility,
  selectedProcessId,
  intl,
}: {
  result: HashMap<FieldOnView>;
  field: Field;
  fieldVisibility: HashMap<VisibleFieldModel[]>;
  selectedProcessId: Process;
  intl: IntlShape;
}): void {
  result[field.fieldName] = {
    ...field,
    disabled: !field.canBeDisabled,
    visible: isFieldVisible({
      field,
      fieldVisibility,
      selectedProcessId,
    }),
    label: formatTranslation(intl, unsafeTranslationKey(field.labelId) ),
  };
}

export function isFieldVisible({
  field,
  fieldVisibility,
  selectedProcessId,
}: {
  field: Field;
  fieldVisibility: HashMap<VisibleFieldModel[]>;
  selectedProcessId: Process;
}): boolean {
  return Boolean(
    field.canBeDisabled
      ? (fieldVisibility[selectedProcessId] || []).find(
          (visibleField) =>
            visibleField.fieldName === field.fieldName &&
            !visibleField.deleted
        )
      : field.processes.includes(selectedProcessId)
  );
}
